import { clamp, HintAnimation } from '../hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  orbiTemplate,
  PRESSED,
  Refs,
  renderCamera,
  renderCursor,
  ORBI_FACE_SECONDS,
  renderOrbi,
  stage,
} from './scene-kit';

/**
 * Portal-Hinweis 5 „Verbinde dein LMS" — Port des Claude-Design-Films
 * `LMS Verbinden Loop.dc.html` (Projekt „Portal-Hinweis 5", Datei
 * `hinweis-5-lms-loop.jsx`). Menü „Integrationen" → Gruppe „Lernplattform —
 * Datenabgleich" → Marker-Kreise um „Moodle Plugin" und „ILIAS".
 * Bühne 640×354, 8,0 s Loop; T = Abspielzeit / 1,6.
 */

const STRETCH = 1.6;
const AUTHORED = 5.0;

// Szenenanfänge in Autorenzeit (Summe der `nat`-Längen aus OM_SCENES).
const CUES = { Klick: 0.8, Gruppen: 1.3, Markieren: 2.3, Orbi: 3.6, Loop: 4.3 };

// Geometrie: Navigation links, Seite rechts (Titel frei auf dem Verlauf, darunter eine Glasfläche)
const NX = 14, NY = 18, NW = 150, NH = 318;
const NAV: [string, string][] = [
  ['agent', 'KI-Agenten'],
  ['brain', 'Wissensbasis'],
  ['brush', 'Chat Styling'],
  ['plug', 'Integrationen'],
  ['chart', 'Nutzung'],
];
const SEL = 3;
const NAV_Y = (i: number) => 12 + i * 38;
const RX = 176, RY = 62, RW = 450, RH = 274;
const TW = 95, TH = 80;
const TX = (i: number) => 20 + i * (TW + 10);
const G1 = { head: 22, tile: 42 }, G2 = { head: 146, tile: 166 };
const MARKS: Record<string, { bg: string; label: string; size: number }> = {
  lti: { bg: '#8A97AB', label: 'LTI', size: 7.5 },
  moodle: { bg: '#F98012', label: 'm', size: 15 },
  ilias: { bg: '#1D3F72', label: 'i', size: 15 },
  edx: { bg: '#3A4250', label: 'edX', size: 7.5 },
};
const SYNC: [string, string][] = [['moodle', 'Moodle Plugin'], ['moodle', 'Moodle Sync'], ['ilias', 'ILIAS'], ['edx', 'OPEN edX']];
const HI = [0, 2]; // markierte Kacheln
const SEEDS = [0.7, 2.4];

/** Leicht unregelmäßige Ellipse, wie mit Marker gezogen: Anfang oben links, Ende überlappt nach außen. */
function markerPath(cx: number, cy: number, rx: number, ry: number, seed: number): string {
  const N = 64, a0 = -2.25, span = Math.PI * 2 + 0.55;
  let d = '';
  for (let k = 0; k <= N; k++) {
    const u = k / N, a = a0 + span * u;
    const f = 1 + 0.035 * Math.sin(2 * a + seed) + 0.018 * Math.sin(5 * a + seed * 1.7) + 0.06 * u;
    const x = cx + rx * f * Math.cos(a), y = cy + ry * f * Math.sin(a);
    d += `${k ? 'L' : 'M'}${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  return d;
}

function mark(kind: string): string {
  const m = MARKS[kind];
  return (
    `<div style="width:24px;height:24px;border-radius:999px;background:${m.bg};display:flex;align-items:center;justify-content:center;` +
    `font-size:${m.size}px;font-weight:700;letter-spacing:${m.label.length > 1 ? '0.02em' : '-0.02em'};color:#fff;line-height:1;` +
    `box-shadow:inset 0 1px 1px rgb(255 255 255 / .35), 0 3px 6px -3px rgb(38 65 112 / .45);padding-bottom:${m.size > 10 ? 2 : 0}px;box-sizing:border-box;">${m.label}</div>`
  );
}

function tile(ref: string, x: number, y: number, kind: string, label: string): string {
  return (
    `<div data-r="${ref}" style="${abs(x, y, TW, TH, 'opacity:0;')}">` +
    `<div data-r="${ref}Face" style="position:absolute;inset:0;border-radius:18px;background:linear-gradient(150deg, rgb(255 255 255 / .34), rgb(255 255 255 / .12));box-shadow:inset 0 1px 1px rgb(255 255 255 / .85), inset 0 -1px 1px rgb(52 76 117 / .10);"></div>` +
    `<div style="${abs(12, 12, 24, 24)}">${mark(kind)}</div>` +
    `<div style="${abs(12, 50, TW - 16, 18, `font-size:12px;font-weight:600;color:${C.text};white-space:nowrap;`)}">${label}</div>` +
    `</div>`
  );
}

function template(uid: string): string {
  const nav = NAV.map(
    ([ic, label], i) =>
      `<div style="${abs(8, NAV_Y(i), NW - 16, 34, 'display:flex;')}">` +
      (i === SEL ? `<div data-r="navSel" style="position:absolute;inset:0;border-radius:12px;opacity:0;${PRESSED}"></div>` : '') +
      `<div ${i === SEL ? 'data-r="navLabel"' : ''} style="position:relative;flex:1;display:flex;align-items:center;gap:9px;padding:0 10px;font-size:12.5px;font-weight:500;color:${C.text};white-space:nowrap;">${icon(ic, 16)}${label}</div></div>`,
  ).join('');

  const head = (ref: string, y: number, text: string) =>
    `<div data-r="${ref}" style="${abs(20, y, 300, 14, `opacity:0;font-size:11px;font-weight:500;color:${C.muted};white-space:nowrap;`)}">${text}</div>`;

  const markers = HI.map((ti, h) => {
    const cx = TX(ti) + TW / 2, cy = G2.tile + TH / 2;
    return (
      `<svg data-r="marker" width="${RW}" height="${RH}" style="position:absolute;left:0;top:0;overflow:visible;pointer-events:none;opacity:0;">` +
      `<path data-r="markerPath" d="${markerPath(cx, cy, TW / 2 + 8, TH / 2 + 9, SEEDS[h])}" pathLength="1" fill="none" stroke="${C.accent}" stroke-width="2.6" ` +
      `stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="1 1" stroke-dashoffset="1" transform="rotate(-3 ${cx} ${cy})" opacity="0.92"/></svg>`
    );
  }).join('');

  const page = glass(
    30,
    0.42,
    `left:${RX}px;top:${RY}px;width:${RW}px;height:${RH}px;opacity:0;`,
    head('head1', G1.head, 'Lernplattform — Verbindung') +
      tile('lti', TX(0), G1.tile, 'lti', 'LTI 1.3') +
      head('head2', G2.head, 'Lernplattform — Datenabgleich') +
      SYNC.map(([kind, label], i) => tile('sync', TX(i), G2.tile, kind, label)).join('') +
      markers +
      `<div data-r="orbiBox" style="${abs(RW - 56, G2.head - 18, 30, 30, 'opacity:0;')}">${orbiTemplate('orbi', 30)}</div>`,
    'data-r="page"',
  );

  const title = `<div data-r="title" style="${abs(RX + 12, 24, 260, 28, `opacity:0;font-size:20px;font-weight:600;letter-spacing:-0.02em;color:${C.text};white-space:nowrap;`)}">Integrationen</div>`;

  return stage(glass(26, 0.42, `left:${NX}px;top:${NY}px;width:${NW}px;height:${NH}px;`, nav) + title + page + cursorTemplate(uid));
}

export class ConnectLmsAnimation implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds = AUTHORED * STRETCH;
  /** Schlussbild: beide Kreise, Orbi lacht (T = 4,1). */
  readonly stillAt = 4.1 * STRETCH;

  private refs: Refs = {};

  mount(host: HTMLElement): void {
    this.refs = mountTemplate(host, template(nextUid('al-hint-lms')));
  }

  render(t: number): void {
    this.frame(clamp(t / STRETCH, 0, AUTHORED));
  }

  private one(key: string): HTMLElement {
    return this.refs[key][0];
  }

  private fadeIn(el: HTMLElement, o: number, dy: number): void {
    el.style.opacity = String(o);
    el.style.transform = `translateY(${(1 - o) * dy}px)`;
  }

  /** Ein Bild zur Autorenzeit T — Werte wie `Piece` in hinweis-5-lms-loop.jsx. */
  private frame(T: number): void {
    const { Klick: kl, Gruppen: gr, Markieren: mk, Orbi: ob, Loop: lp } = CUES;
    const live = 1 - MOTION.glide(lp, lp + 0.6, T);

    const c1 = kl + 0.05;
    const k = click(c1, T);

    // Zeiger
    const REST = [598, 318];
    const I = [NX + 72, NY + NAV_Y(SEL) + 18];
    const ct = [0, 0.12, c1 - 0.06, c1 + 0.1, gr + 0.35, 99];
    const pts = [REST, REST, I, I, REST, REST];
    const cx = MOTION.path(ct, pts.map((p) => p[0]), T);
    const cy = MOTION.path(ct, pts.map((p) => p[1]), T);
    renderCursor(this.one('ring'), this.one('arrow'), cx, cy, k.press, k.ring > 0 && k.ring < 1 ? k.ring : 0);

    // Navigation + Seite
    const sel = MOTION.enter(c1, c1 + 0.12, T) * live;
    this.one('navSel').style.opacity = String(sel);
    this.one('navLabel').style.fontWeight = sel > 0.5 ? '600' : '500';
    this.fadeIn(this.one('title'), MOTION.enter(c1 + 0.06, c1 + 0.3, T) * live, 6);
    this.fadeIn(this.one('page'), MOTION.enter(c1 + 0.1, c1 + 0.4, T) * live, 8);

    // Gruppen und Kacheln
    this.fadeIn(this.one('head1'), MOTION.enter(gr, gr + 0.24, T) * live, 5);
    this.fadeIn(this.one('head2'), MOTION.enter(gr + 0.2, gr + 0.44, T) * live, 5);
    const dim = 1 - 0.45 * MOTION.enter(mk + 0.2, mk + 0.6, T) * live;
    const glows = [MOTION.enter(mk + 0.2, mk + 0.55, T) * live, MOTION.enter(mk + 0.75, mk + 1.1, T) * live];
    const setTile = (el: HTMLElement, face: HTMLElement, o: number, glow: number, d: number) => {
      el.style.opacity = String(o * d);
      el.style.transform = `translateY(${(1 - o) * 10}px) scale(${0.96 + 0.04 * o})`;
      face.style.boxShadow =
        `inset 0 1px 1px rgb(255 255 255 / .85), inset 0 -1px 1px rgb(52 76 117 / .10), ` +
        `0 0 0 ${2 * glow}px rgb(59 130 246 / ${0.55 * glow}), 0 0 ${20 * glow}px rgb(59 130 246 / ${0.45 * glow})`;
    };
    setTile(this.one('lti'), this.one('ltiFace'), MOTION.enter(gr + 0.06, gr + 0.36, T) * live, 0, dim);
    this.refs['sync'].forEach((el, i) => {
      const o = MOTION.enter(gr + 0.26 + 0.07 * i, gr + 0.56 + 0.07 * i, T) * live;
      const h = HI.indexOf(i);
      setTile(el, this.refs['syncFace'][i], o, h >= 0 ? glows[h] : 0, h >= 0 ? 1 : dim);
    });

    // Marker-Kreise um Moodle Plugin und ILIAS
    const draws = [MOTION.enter(mk + 0.05, mk + 0.5, T), MOTION.enter(mk + 0.6, mk + 1.05, T)];
    this.refs['marker'].forEach((svg, h) => {
      svg.style.opacity = draws[h] > 0.001 ? String(live) : '0';
      this.refs['markerPath'][h].setAttribute('stroke-dashoffset', String(1 - draws[h]));
    });

    // Orbi
    const orbiO = MOTION.enter(ob, ob + 0.15, T) * live;
    const orbiBox = this.one('orbiBox');
    orbiBox.style.opacity = String(orbiO);
    orbiBox.style.transform = `scale(${0.6 + 0.4 * orbiO})`;
    renderOrbi(this.refs, 'orbi', MOTION.glide(ob + 0.14, ob + 0.14 + ORBI_FACE_SECONDS, T), Math.sin(Math.PI * clamp((T - ob) / 0.35, 0, 1)));

    // Kamera
    const ck = [0, c1, gr + 0.35, gr + 0.95, lp, lp + 0.6, 99];
    const s = MOTION.path(ck, [1, 1.04, 1, 1.3, 1.3, 1, 1], T);
    const fx = MOTION.path(ck, [320, 290, 320, 401, 401, 320, 320], T);
    const fy = MOTION.path(ck, [176, 170, 176, 238, 238, 176, 176], T);
    renderCamera(this.one('camera'), fx, fy, s);
  }
}
